<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Rapport de Caisse</h1>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable && rows.sales">
      <!-- Filters -->
      <v-card flat class="filter-card mb-4">
        <v-card-text class="py-3">
          <v-row align="center" dense>
            <v-col cols="12" sm="6" md="3">
              <v-select :items="stores" item-value="id" item-text="name" placeholder="Emplacement" prepend-inner-icon="mdi-store-outline" v-model="store_id" outlined dense hide-details clearable></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select :items="users" item-value="id" item-text="full_name" placeholder="Agent" v-model="user_id" outlined dense hide-details clearable></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select :items="devices" item-value="id" item-text="name" placeholder="Appareil" v-model="device_id" outlined dense hide-details clearable></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="auto">
              <div class="d-flex gap-2">
                <v-btn color="primary" depressed small @click="filter" :loading="loading" class="mr-1"><v-icon small>mdi-check</v-icon></v-btn>
                <v-btn outlined small @click="reset" :loading="loading" class="mr-1"><v-icon small>mdi-refresh</v-icon></v-btn>
                <v-btn color="error" outlined small @click="exportPDF" class="mr-1"><v-icon small>mdi-file-pdf-box</v-icon></v-btn>
                <v-btn color="success" outlined small @click="exportExcel"><v-icon small>mdi-file-excel</v-icon></v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Summary cards -->
      <v-row class="mb-2">
        <v-col cols="6" md="3">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#194173"><v-icon color="white" size="18">mdi-cash-register</v-icon></div>
            <div class="summary-val">{{ totalSalesHTG | formatNumber }}</div>
            <div class="summary-lbl">Ventes HTG</div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#f68d3b"><v-icon color="white" size="18">mdi-currency-usd</v-icon></div>
            <div class="summary-val">{{ totalSalesUSD | formatNumber }}</div>
            <div class="summary-lbl">Ventes USD</div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#F44336"><v-icon color="white" size="18">mdi-cancel</v-icon></div>
            <div class="summary-val error--text">{{ rows.cancellations.HTG.amount | formatNumber }}</div>
            <div class="summary-lbl">Annulations HTG</div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#FF9800"><v-icon color="white" size="18">mdi-undo</v-icon></div>
            <div class="summary-val warning--text">{{ rows.returns.HTG.amount | formatNumber }}</div>
            <div class="summary-lbl">Retours HTG</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Caisse table -->
      <v-card flat class="table-card">
        <div class="table-responsive">
          <table class="report-table">
            <thead>
              <tr>
                <th class="text-left">TYPE</th>
                <th class="text-right">HTG</th>
                <th class="text-right">USD</th>
              </tr>
            </thead>
            <tbody>
              <!-- Payment methods -->
              <tr class="section-header"><td colspan="3">Ventes par méthode de paiement</td></tr>
              <tr>
                <td class="text-left"><v-icon small color="success" class="mr-2">mdi-cash</v-icon>Cash</td>
                <td class="text-right">{{ getMethodAmount('Cash', 'HTG') | formatNumber }}</td>
                <td class="text-right">{{ getMethodAmount('Cash', 'USD') | formatNumber }}</td>
              </tr>
              <tr>
                <td class="text-left"><v-icon small color="info" class="mr-2">mdi-credit-card</v-icon>Carte</td>
                <td class="text-right">{{ getMethodAmount('Carte', 'HTG') | formatNumber }}</td>
                <td class="text-right">{{ getMethodAmount('Carte', 'USD') | formatNumber }}</td>
              </tr>
              <tr>
                <td class="text-left"><v-icon small color="primary" class="mr-2">mdi-checkbook</v-icon>Chèque</td>
                <td class="text-right">{{ getMethodAmount('Cheque', 'HTG') | formatNumber }}</td>
                <td class="text-right">{{ getMethodAmount('Cheque', 'USD') | formatNumber }}</td>
              </tr>
              <tr>
                <td class="text-left"><v-icon small color="deep-purple" class="mr-2">mdi-note-text-outline</v-icon>Crédit</td>
                <td class="text-right">{{ getMethodAmount('Credit', 'HTG') | formatNumber }}</td>
                <td class="text-right">{{ getMethodAmount('Credit', 'USD') | formatNumber }}</td>
              </tr>
              <tr>
                <td class="text-left"><v-icon small color="primary" class="mr-2">mdi-contactless-payment</v-icon>KL PAY</td>
                <td class="text-right">{{ getMethodAmount('KL Pay', 'HTG') | formatNumber }}</td>
                <td class="text-right">{{ getMethodAmount('KL Pay', 'USD') | formatNumber }}</td>
              </tr>
              <!-- Méthodes legacy : visibles seulement sur les périodes d'avant la migration KL Pay -->
              <tr v-if="hasMethod('NATCASH')">
                <td class="text-left"><img :src="natcashMenuIcon" alt="" class="payment-menu-icon mr-2" />NATCASH</td>
                <td class="text-right">{{ getMethodAmount('NATCASH', 'HTG') | formatNumber }}</td>
                <td class="text-right">{{ getMethodAmount('NATCASH', 'USD') | formatNumber }}</td>
              </tr>
              <tr v-if="hasMethod('MONCASH')">
                <td class="text-left"><img :src="moncashMenuIcon" alt="" class="payment-menu-icon mr-2" />MONCASH</td>
                <td class="text-right">{{ getMethodAmount('MONCASH', 'HTG') | formatNumber }}</td>
                <td class="text-right">{{ getMethodAmount('MONCASH', 'USD') | formatNumber }}</td>
              </tr>
              <!-- Méthodes personnalisées / additionnelles -->
              <tr v-for="m in customMethods" :key="m">
                <td class="text-left"><v-icon small color="grey" class="mr-2">mdi-cash-multiple</v-icon>{{ m }}</td>
                <td class="text-right">{{ getMethodAmount(m, 'HTG') | formatNumber }}</td>
                <td class="text-right">{{ getMethodAmount(m, 'USD') | formatNumber }}</td>
              </tr>

              <!-- Subtotal -->
              <tr class="subtotal-row">
                <td class="text-left font-weight-bold">Total Ventes</td>
                <td class="text-right font-weight-bold">{{ totalSalesHTG | formatNumber }}</td>
                <td class="text-right font-weight-bold">{{ totalSalesUSD | formatNumber }}</td>
              </tr>

              <!-- Cancellations & Returns -->
              <tr class="section-header"><td colspan="3">Ajustements</td></tr>
              <tr>
                <td class="text-left"><v-icon small color="error" class="mr-2">mdi-cancel</v-icon>Annulations</td>
                <td class="text-right error--text">{{ rows.cancellations.HTG.amount | formatNumber }}</td>
                <td class="text-right error--text">{{ rows.cancellations.USD.amount | formatNumber }}</td>
              </tr>
              <tr>
                <td class="text-left"><v-icon small color="warning" class="mr-2">mdi-undo</v-icon>Retours</td>
                <td class="text-right warning--text">{{ rows.returns.HTG.amount | formatNumber }}</td>
                <td class="text-right warning--text">{{ rows.returns.USD.amount | formatNumber }}</td>
              </tr>

              <!-- Grand total -->
              <tr class="grand-total-row">
                <td class="text-left font-weight-bold">NET</td>
                <td class="text-right font-weight-bold">{{ netHTG | formatNumber }}</td>
                <td class="text-right font-weight-bold">{{ netUSD | formatNumber }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'

export default {
  name: 'ClosingView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    natcashMenuIcon: require('../assets/natcash_menu_new.svg'),
    moncashMenuIcon: require('../assets/moncash_menu.svg'),
    items: [{ text: 'Rapport Caisse', disabled: false, href: '/closing' }],
    rows: [],
    user_id: '',
    device_id: '',
    enable: false,
    loading: false,
    filter_rate_id: 1,
    store_id: '',
    category_id: '',
    business_id: 0,
    stores: [],
    devices: [],
    users: [],
  }),

  computed: {
    totalSalesHTG() {
      if (!this.rows.sales) return 0
      var t = 0; var vals = this.rows.sales.HTG.values
      Object.keys(vals).forEach(function (k) { t += parseFloat(vals[k].amount) || 0 })
      return t
    },
    totalSalesUSD() {
      if (!this.rows.sales) return 0
      var t = 0; var vals = this.rows.sales.USD.values
      Object.keys(vals).forEach(function (k) { t += parseFloat(vals[k].amount) || 0 })
      return t
    },
    netHTG() {
      return this.totalSalesHTG - Math.abs(parseFloat(this.rows.cancellations.HTG.amount) || 0) - Math.abs(parseFloat(this.rows.returns.HTG.amount) || 0)
    },
    netUSD() {
      return this.totalSalesUSD - Math.abs(parseFloat(this.rows.cancellations.USD.amount) || 0) - Math.abs(parseFloat(this.rows.returns.USD.amount) || 0)
    },
    customMethods() {
      if (!this.rows.sales) return []
      var knownAliases = [
        'cash', 'espece', 'espèce', 'comptant',
        'carte', 'card',
        'cheque', 'chèque', 'check',
        'credit', 'crédit',
        'kl pay',
        'natcash',
        'moncash'
      ]
      var methodsSet = new Set()
      var self = this
      ;['HTG', 'USD'].forEach(function (cur) {
        if (self.rows.sales[cur] && self.rows.sales[cur].values) {
          Object.keys(self.rows.sales[cur].values).forEach(function (k) {
            if (!knownAliases.includes(k.toLowerCase()) && (parseFloat(self.rows.sales[cur].values[k].amount) || 0) !== 0) {
              methodsSet.add(k)
            }
          })
        }
      })
      return Array.from(methodsSet)
    },
  },

  methods: {
    getMethodAmount(name, cur) {
      if (!this.rows.sales || !this.rows.sales[cur] || !this.rows.sales[cur].values) return 0
      var vals = this.rows.sales[cur].values
      var aliases = {
        'Carte': ['Carte', 'Card', 'carte', 'card'],
        'Cash': ['Cash', 'cash', 'Espece', 'Espèce', 'espece', 'espèce', 'Comptant', 'comptant'],
        'Cheque': ['Cheque', 'Chèque', 'cheque', 'chèque', 'Check', 'check'],
        'Credit': ['Credit', 'Crédit', 'credit', 'crédit'],
        'KL Pay': ['KL Pay', 'KL PAY', 'kl pay', 'Kl Pay'],
        'NATCASH': ['NATCASH', 'Natcash', 'natcash'],
        'MONCASH': ['MONCASH', 'Moncash', 'moncash']
      }
      var targets = (aliases[name] || [name]).map(function (s) { return s.toLowerCase() })
      var sum = 0
      Object.keys(vals).forEach(function (k) {
        if (targets.includes(k.toLowerCase())) {
          sum += parseFloat(vals[k].amount) || 0
        }
      })
      return sum
    },

    hasMethod(name) {
      return (parseFloat(this.getMethodAmount(name, 'HTG')) || 0) !== 0 ||
        (parseFloat(this.getMethodAmount(name, 'USD')) || 0) !== 0
    },

    getDateParams() {
      var isValid = function (v) { return v && v !== 'null' && v.trim() !== '' }
      return {
        from: isValid(localStorage.debut_periode) ? localStorage.debut_periode : '',
        to: isValid(localStorage.fin_periode) ? localStorage.fin_periode : ''
      }
    },

    load() {
      var self = this; var d = this.getDateParams()
      this.$axios.get('/reports/closure?from=' + d.from + '&to=' + d.to)
        .then(function (r) { self.rows = r.data.result; self.enable = true })
        .catch(function () { self.enable = true })
    },

    filter() {
      var self = this; var d = this.getDateParams()
      var params = 'from=' + d.from + '&to=' + d.to
      // L'API closure attend le paramètre `store` (validé contre les magasins du business)
      if (this.store_id) params += '&store=' + this.store_id
      if (this.user_id) params += '&user_id=' + this.user_id
      if (this.category_id) params += '&category_id=' + this.category_id
      if (this.device_id) params += '&device_id=' + this.device_id
      self.enable = false
      this.$axios.get('/reports/closure?' + params)
        .then(function (r) { self.rows = r.data.result; self.enable = true })
        .catch(function () { self.enable = true })
    },

    reset() { this.store_id = ''; this.user_id = ''; this.device_id = ''; this.load() },

    buildTableData() {
      var r = this.rows
      var lines = []
      lines.push({ type: 'Cash', htg: parseFloat(this.getMethodAmount('Cash', 'HTG')) || 0, usd: parseFloat(this.getMethodAmount('Cash', 'USD')) || 0 })
      lines.push({ type: 'Carte', htg: parseFloat(this.getMethodAmount('Carte', 'HTG')) || 0, usd: parseFloat(this.getMethodAmount('Carte', 'USD')) || 0 })
      lines.push({ type: 'Chèque', htg: parseFloat(this.getMethodAmount('Cheque', 'HTG')) || 0, usd: parseFloat(this.getMethodAmount('Cheque', 'USD')) || 0 })
      lines.push({ type: 'Crédit', htg: parseFloat(this.getMethodAmount('Credit', 'HTG')) || 0, usd: parseFloat(this.getMethodAmount('Credit', 'USD')) || 0 })
      lines.push({ type: 'KL PAY', htg: parseFloat(this.getMethodAmount('KL Pay', 'HTG')) || 0, usd: parseFloat(this.getMethodAmount('KL Pay', 'USD')) || 0 })
      // Méthodes legacy : exportées seulement si la période contient des paiements d'avant la migration KL Pay
      if (this.hasMethod('NATCASH')) lines.push({ type: 'NATCASH', htg: parseFloat(this.getMethodAmount('NATCASH', 'HTG')) || 0, usd: parseFloat(this.getMethodAmount('NATCASH', 'USD')) || 0 })
      if (this.hasMethod('MONCASH')) lines.push({ type: 'MONCASH', htg: parseFloat(this.getMethodAmount('MONCASH', 'HTG')) || 0, usd: parseFloat(this.getMethodAmount('MONCASH', 'USD')) || 0 })
      if (this.customMethods && this.customMethods.length) {
        var self = this
        this.customMethods.forEach(function (m) {
          lines.push({ type: m, htg: parseFloat(self.getMethodAmount(m, 'HTG')) || 0, usd: parseFloat(self.getMethodAmount(m, 'USD')) || 0 })
        })
      }
      lines.push({ type: 'TOTAL VENTES', htg: this.totalSalesHTG, usd: this.totalSalesUSD, bold: true })
      lines.push({ type: 'Annulations', htg: parseFloat(r.cancellations.HTG.amount) || 0, usd: parseFloat(r.cancellations.USD.amount) || 0 })
      lines.push({ type: 'Retours', htg: parseFloat(r.returns.HTG.amount) || 0, usd: parseFloat(r.returns.USD.amount) || 0 })
      lines.push({ type: 'NET', htg: this.netHTG, usd: this.netUSD, bold: true })
      return lines
    },

    async exportPDF() {
      var self = this
      var { jsPDF } = await import('jspdf')
      var doc = new jsPDF('p', 'mm', 'a4')
      var ml = 16, mr = 194, cw = mr - ml
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var d = self.getDateParams()
      var primary = [25, 65, 115], dark = [30, 41, 59], muted = [100, 116, 139], light = [248, 250, 252], border = [220, 225, 230]

      function fmt(v) { if (!v || isNaN(v)) return '0.00'; var p = Math.abs(parseFloat(v)).toFixed(2).split('.'); p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' '); return (parseFloat(v) < 0 ? '-' : '') + p.join('.') }

      // Header
      doc.setFillColor(...primary); doc.rect(0, 0, 210, 24, 'F')
      doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
      doc.text('RAPPORT DE CAISSE', ml, 12)
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(200, 215, 235)
      doc.text(d.from + ' — ' + d.to, mr, 9, { align: 'right' })
      if (biz.name) doc.text(biz.name, mr, 15, { align: 'right' })

      var y = 34
      var cols = [
        { label: 'TYPE', w: 80, align: 'left' },
        { label: 'HTG', w: 45, align: 'right' },
        { label: 'USD', w: 45, align: 'right' },
      ]
      var cx = ml; cols.forEach(function (c) { c.x = cx; cx += c.w })
      var rowH = 8

      // Header
      doc.setFillColor(...primary); doc.rect(ml, y, cw, rowH + 1, 'F')
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
      cols.forEach(function (c) { var tx = c.align === 'right' ? c.x + c.w - 3 : c.x + 3; doc.text(c.label, tx, y + 5.5, { align: c.align }) })
      y += rowH + 1

      var lines = self.buildTableData()
      lines.forEach(function (line, idx) {
        if (line.bold) {
          doc.setFillColor(...light); doc.rect(ml, y, cw, rowH, 'F')
          doc.setDrawColor(...primary); doc.setLineWidth(0.3); doc.line(ml, y, ml + cw, y)
        } else if (idx % 2 === 0) {
          doc.setFillColor(250, 251, 253); doc.rect(ml, y, cw, rowH, 'F')
        }
        doc.setDrawColor(...border); doc.setLineWidth(0.1); doc.line(ml, y + rowH, mr, y + rowH)

        doc.setFontSize(8); doc.setFont('helvetica', line.bold ? 'bold' : 'normal'); doc.setTextColor(...dark)
        doc.text(line.type, ml + 3, y + 5.5)
        doc.text(fmt(line.htg), cols[1].x + cols[1].w - 3, y + 5.5, { align: 'right' })
        doc.text(fmt(line.usd), cols[2].x + cols[2].w - 3, y + 5.5, { align: 'right' })
        y += rowH
      })

      // Footer
      doc.setDrawColor(...border); doc.setLineWidth(0.2); doc.line(ml, 280, mr, 280)
      doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...primary); doc.text('KES LAKAY', ml, 285)
      doc.setFont('helvetica', 'normal'); doc.setTextColor(...muted)
      doc.text('Page 1 / 1', 105, 285, { align: 'center' })
      var now = new Date(); doc.text(now.toLocaleDateString('fr-FR') + ' ' + now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), mr, 285, { align: 'right' })

      doc.save('rapport_caisse.pdf')
    },

    exportExcel() {
      var self = this
      var XLSX = require('xlsx')
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var d = self.getDateParams()
      var wsData = [['RAPPORT DE CAISSE'], [biz.name || ''], ['Du ' + d.from + ' au ' + d.to], []]
      wsData.push(['Type', 'HTG', 'USD'])

      var lines = self.buildTableData()
      lines.forEach(function (l) { wsData.push([l.type, Math.round(l.htg * 100) / 100, Math.round(l.usd * 100) / 100]) })

      var wb = XLSX.utils.book_new(); var ws = XLSX.utils.aoa_to_sheet(wsData)
      ws['!cols'] = [{ wch: 24 }, { wch: 18 }, { wch: 18 }]
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 4, c: 0 }, e: { r: 4, c: 2 } }) }
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } }, { s: { r: 2, c: 0 }, e: { r: 2, c: 2 } }]
      for (var r = 5; r < wsData.length; r++) { for (var c = 1; c <= 2; c++) { var cell = ws[XLSX.utils.encode_cell({ r: r, c: c })]; if (cell && typeof cell.v === 'number') { cell.t = 'n'; cell.z = '#,##0.00' } } }
      XLSX.utils.book_append_sheet(wb, ws, 'Caisse'); XLSX.writeFile(wb, 'rapport_caisse.xlsx')
    },

    async getStores() {
      this.business_id = JSON.parse(localStorage.business).id
      var self = this
      await this.$axios.get('/stores?orderasc=name&business_id=' + this.business_id).then(function (r) { self.stores = r.data })
    },
    async getUsers() {
      var self = this
      await this.$axios.get('/users').then(function (r) { self.users = r.data })
    },
    async getDevices() {
      this.business_id = JSON.parse(localStorage.business).id
      var self = this
      await this.$axios.get('/devices?business_id=' + this.business_id).then(function (r) { self.devices = r.data }).catch(function () {})
    },
  },

  created() {
    this.filter_rate_id = localStorage.filter_rate_id
    this.load(); this.getStores(); this.getUsers(); this.getDevices()
  },
}
</script>

<style scoped>
.summary-card { border: 1px solid #e8ecf1 !important; border-radius: 10px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; padding: 16px; text-align: center; }
.summary-icon-sm { width: 36px; height: 36px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.summary-val { font-size: 1.125rem; font-weight: 700; color: #1a2332; }
.summary-lbl { font-size: 0.75rem; color: #64748b; margin-top: 2px; }

.filter-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; }
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; overflow: hidden; }
.table-responsive { overflow-x: auto; }

.report-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.report-table thead th { background: #f8fafc; font-weight: 600; font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; padding: 12px 16px; border-bottom: 2px solid #e8ecf1; white-space: nowrap; }
.report-table tbody td { padding: 12px 16px; border-bottom: 1px solid #f1f5f9; white-space: nowrap; }
.report-table tbody tr:hover { background-color: rgba(25, 65, 115, 0.03); }

.section-header td { font-size: 0.6875rem !important; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8 !important; background: #fafbfc; padding: 8px 16px !important; border-bottom: 1px solid #e8ecf1 !important; }
.subtotal-row td { background: #f1f5f9 !important; border-top: 2px solid #cbd5e1 !important; border-bottom: 2px solid #cbd5e1 !important; }
.grand-total-row td { background: #eef2ff !important; border-top: 2px solid #194173 !important; color: #194173 !important; font-size: 0.9375rem !important; }

.gap-2 { gap: 4px; }

.payment-menu-icon {
  width: 18px;
  height: 18px;
  vertical-align: middle;
  display: inline-block;
  object-fit: contain;
}
</style>
